'use client';

import React, { useEffect, useState } from 'react';

import { useProofOfWork } from '@repo/editor/src/hooks/useProofOfWork';
import { withEventScope } from '@repo/event-processor';
import { fireToast } from '@repo/ui/components/Toast';
import { H1PlusSmall } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import textGenApi from 'global/textGenApi';
import { FlexColumn } from 'styles/layouts';

import AiTextToolForm from '../aiTextToolForm/AiTextToolForm';
import { AiTextToolFormSubmitOptions } from '../aiTextToolForm/types';
import TextBlock from '../shared/TextBlock/TextBlock';

import AiTextToolResults from './AiTextToolResults';
import {
  Background,
  Wrapper,
  DescriptionWrapper,
  HeaderCenter,
} from './styles';
import { AiTextToolProps, Result } from './types';
import { formatDynamicOptions } from './utils';

const AiTextTool: React.FC<AiTextToolProps> = ({
  title,
  description,
  toolConfigs,
  titleResults,
  buttonLabel,
  background,
  numberOfOutputs,
  textType,
  promptId,
}) => {
  const [results, setResults] = useState<Result[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { challenge, fetchChallenge, isChallengeReady } = useProofOfWork();

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  const handleFormSubmit = async (
    options: AiTextToolFormSubmitOptions
  ): Promise<void> => {
    setIsLoading(true);
    const dynamicOptions = formatDynamicOptions(options);

    const response = await textGenApi.generateTextPublic(
      {
        promptId: promptId ?? 0,
        variables: [
          {
            name: 'num_output',
            value: `${numberOfOutputs}`,
          },
          {
            name: 'text_type',
            value: textType ?? '',
          },
        ],
        dynamicOptions,
      },
      challenge ?? undefined
    );
    if (response.success) {
      setResults(
        response.result.results.map((text) => ({
          text,
        }))
      );
    } else if (response.error === 'TOO_MANY_REQUESTS') {
      fireToast({
        error: true,
        duration: 5000,
        label:
          'A lot of other people are currently generating texts, create a free account to generate your texts now.',
      });
    } else {
      fireToast({
        error: true,
        duration: 5000,
        label: 'We could not generate your text with the input you provided.',
      });
    }
    setIsLoading(false);
    fetchChallenge();
  };

  return (
    <Background background={background?.url}>
      <Wrapper>
        <FlexColumn gap="20px">
          <HeaderCenter>
            <H1PlusSmall>{title}</H1PlusSmall>
          </HeaderCenter>
          {description && (
            <DescriptionWrapper>
              <TextBlock color={themeStyle.varInkMedium} text={description} />
            </DescriptionWrapper>
          )}
        </FlexColumn>
        {toolConfigs && (
          <AiTextToolForm
            formSchema={toolConfigs}
            isLoading={isLoading || !isChallengeReady}
            onSubmit={handleFormSubmit}
          />
        )}
        {!!results.length && (
          <AiTextToolResults
            buttonText={buttonLabel}
            isLoading={isLoading}
            results={results}
            title={titleResults}
          />
        )}
      </Wrapper>
    </Background>
  );
};

export default withEventScope(AiTextTool, { feature: 'aiTextTool' });
